import type { AcmMetricKey } from '../types/kpi.js';

/**
 * KPI 指標1件の定義情報。ビジネスサイド向けの「考え方」とエンジニア向けの「技術定義」を持つ。
 * label / block は API レスポンス columns（ACM_COLUMNS 由来の単一ソース）から取得するため、
 * 定義データは type / businessDescription / technicalSource / validation に限定する。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 日次KPI各指標のビジネス定義・技術定義を一元管理する型
 * @graph-connects none
 */
export interface KpiMetricDefinition {
  type: 'stock' | 'flow';
  businessDescription: string;
  technicalSource: {
    database: string;
    tables: string[];
    logic: string;
  };
  validation?: string;
}

/**
 * 日次KPI 25指標の定義一覧。ビジネス定義と技術定義の単一ソース。
 * Record<AcmMetricKey, ...> により AcmMetricKey に指標が追加されるとコンパイルエラーで検知できる。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 日次KPIの25指標について、ビジネス定義（指標の考え方）と技術定義（ソースDB・テーブル・計算ロジック）を保持する
 * @graph-connects none
 */
export const KPI_DEFINITIONS: Record<AcmMetricKey, KpiMetricDefinition> = {
  // ========== 会員数ブロック ==========
  accountCount: {
    type: 'stock',
    businessDescription:
      'その日時点で存在する全アカウントの累計数。無料会員登録（メールアドレス登録）を完了したユーザーの総数で、サブスク契約の有無は問わない。退会しても減らない（累計）。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL)',
      tables: ['users'],
      logic:
        "users テーブルの created_at（UTC→JST変換）がその日以前のレコード数をCOUNTする。\nSELECT count(*) FROM users WHERE (created_at AT TIME ZONE 'UTC' AT TIME ZONE 'Asia/Tokyo')::date <= 対象日",
    },
    validation: '単調非減少（前日以上であること）',
  },
  accountNew: {
    type: 'flow',
    businessDescription:
      'その日に新しくアカウント登録を完了したユーザーの数。前日との差分（accountCount の日次増分）に等しい。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL)',
      tables: ['users'],
      logic:
        "users テーブルの created_at（UTC→JST変換）がその日と一致するレコード数。\nSELECT count(*) FROM users WHERE (created_at AT TIME ZONE 'UTC' AT TIME ZONE 'Asia/Tokyo')::date = 対象日",
    },
    validation: 'accountCount の日次差分と一致すること',
  },
  subscriptionMemberCount: {
    type: 'stock',
    businessDescription:
      'その日時点でサブスクリプション契約が有効な会員の数。active（利用中）・cancel_requested（解約申請中）・waiting（待機中）のすべてのステータスを含む。cancelled_at が到来済みの場合は除外。サービス提供の開始有無は問わない。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL)',
      tables: ['user_subscription_plan_relations'],
      logic:
        "created_at（UTC→JST変換）がその日以前 かつ cancelled_at が NULL または未到来のレコード数をCOUNTする。\nSELECT count(*) FROM user_subscription_plan_relations\nWHERE (created_at AT TIME ZONE 'UTC' AT TIME ZONE 'Asia/Tokyo')::date <= 対象日 AND (cancelled_at IS NULL OR (cancelled_at AT TIME ZONE 'UTC' AT TIME ZONE 'Asia/Tokyo')::date > 対象日)",
    },
  },
  serviceStartedCount: {
    type: 'stock',
    businessDescription:
      'サブスク会員のうち、現在の契約での初回サブスクリプション課金（subscription_fee）が完了している会員数。実質的なアクティブ会員数に相当する。再登録ユーザーは再登録後の初回課金完了までサービス未開始として扱う。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL)',
      tables: ['user_subscription_plan_relations', 'payment_histories', 'payment_types'],
      logic:
        "subscriptionMemberCount の対象レコードのうち、そのサブスク契約期間内（created_at以降〜次の契約のcreated_at未満、JST date粒度）に payment_type.code = 'subscription_fee' の課金履歴が存在し、その最初の purchase_date がその日以前のものをCOUNTする。LEAD window関数で課金を正しい契約に帰属させる。同日に解約→再登録が発生した場合、課金は新契約側に帰属する（>= cdt による新契約寄せ）。",
    },
  },
  serviceNotStartedCount: {
    type: 'stock',
    businessDescription:
      'サブスク会員のうち、現在の契約でまだ初回課金が完了していない会員数。subscriptionMemberCount − serviceStartedCount で算出される。waiting ステータス、active だがまだ初回課金が走っていない会員、および再登録後に未課金の会員が含まれる。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL)',
      tables: ['user_subscription_plan_relations', 'payment_histories'],
      logic:
        'アプリ側で subscriptionMemberCount − serviceStartedCount として算出する。SQLレベルではなくスナップショット組み立て時に導出。',
    },
    validation: 'subscription_member_count − service_started_count と一致すること',
  },
  subscriptionNew: {
    type: 'flow',
    businessDescription:
      'その日に新しくサブスクリプション契約を開始した会員の数。プラン登録日ベースで計上する。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL)',
      tables: ['user_subscription_plan_relations'],
      logic:
        "created_at（UTC→JST変換）がその日と一致するレコード数。\nSELECT count(*) FROM user_subscription_plan_relations WHERE (created_at AT TIME ZONE 'UTC' AT TIME ZONE 'Asia/Tokyo')::date = 対象日",
    },
  },
  subscriptionServiceStartNew: {
    type: 'flow',
    businessDescription:
      'その日に初めてサブスクリプション課金が完了し、サービス提供が開始された会員の数。初回課金日ベースで計上する。再登録ユーザーの再開始も別カウントとして計上される。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL)',
      tables: ['user_subscription_plan_relations', 'payment_histories', 'payment_types'],
      logic:
        '各サブスク契約期間内（JST date粒度、同日境界は新契約寄せ）での最初の subscription_fee 課金日がその日と一致するレコード数をCOUNTする。LEAD window関数で課金を正しい契約に帰属させるため、再登録ユーザーの再開始も計上される。',
    },
  },
  subscriptionCancelled: {
    type: 'flow',
    businessDescription:
      'その日にサブスクリプションの解約が確定した（cancelled_at が到来した）会員の数。解約申請日ではなく、実際の解約確定日ベース。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL)',
      tables: ['user_subscription_plan_relations'],
      logic:
        "cancelled_at（UTC→JST変換）がその日と一致するレコード数。\nSELECT count(*) FROM user_subscription_plan_relations WHERE (cancelled_at AT TIME ZONE 'UTC' AT TIME ZONE 'Asia/Tokyo')::date = 対象日",
    },
  },
  // ========== スタイリングブロック ==========
  stylingOpening: {
    type: 'stock',
    businessDescription:
      'その日の開始時点で未完了（スタイリスト対応待ち）のスタイリング案件数。前日の期末残数（stylingClosing）がそのまま翌日の期首残になる。',
    technicalSource: {
      database: '（導出値）',
      tables: [],
      logic:
        'SQLでは期末残のみを算出し、期首残はアプリ側で前日の期末残（styling_closing）の LAG として導出する。初日の期首残は 0。',
    },
  },
  stylingNew: {
    type: 'flow',
    businessDescription:
      'その日に新しく発生したスタイリング案件の数。ユーザーがアイテムリクエストを送信すると1件計上される。',
    technicalSource: {
      database: 'phoenix_styling (MySQL)',
      tables: ['styling_histories'],
      logic:
        'created_at（UTC+9h でJST変換）がその日と一致し、service_id = 3（メンズ）のレコード数。\nSELECT COUNT(*) FROM styling_histories WHERE DATE(created_at + INTERVAL 9 HOUR) = 対象日 AND service_id = 3',
    },
  },
  stylingCompleted: {
    type: 'flow',
    businessDescription:
      'その日にスタイリストが選定を完了した案件の数。ステータスが STYLING_COMPLETED に変わった日で計上する。',
    technicalSource: {
      database: 'phoenix_styling (MySQL)',
      tables: ['styling_histories'],
      logic:
        "status = 'STYLING_COMPLETED' かつ completed_at（UTC+9h）がその日と一致するレコード数。",
    },
  },
  stylingCancelled: {
    type: 'flow',
    businessDescription:
      'その日にキャンセルされたスタイリング案件の数。ユーザー都合の解約などで案件が取り消された場合に計上される。',
    technicalSource: {
      database: 'phoenix_styling (MySQL)',
      tables: ['styling_histories'],
      logic: "status = 'CANCELED' かつ updated_at（UTC+9h）がその日と一致するレコード数。",
    },
  },
  stylingClosing: {
    type: 'stock',
    businessDescription:
      'その日の終了時点で未完了のスタイリング案件数。期首残 + 新規 − 完了 − キャンセル と一致する（恒等式で整合性を検証している）。',
    technicalSource: {
      database: 'phoenix_styling (MySQL)',
      tables: ['styling_histories'],
      logic:
        'created_at がその日以前 かつ completed_at が NULL またはその日より後 かつ status が CANCELED でないか、CANCELED の updated_at がその日より後のレコード数。',
    },
    validation:
      'styling_opening + styling_new − styling_completed − styling_cancelled と一致すること',
  },
  // ========== チケット・返却ブロック ==========
  ticketOpening: {
    type: 'stock',
    businessDescription:
      'その日の開始時点で有効（未使用・未失効）な交換チケットの総数。前日のチケット期末残数がそのまま翌日の期首残になる。',
    technicalSource: {
      database: '（導出値）',
      tables: [],
      logic:
        'スタイリング期首残と同様、アプリ側で前日の ticket_closing の LAG として導出する。初日の期首残は 0。',
    },
  },
  ticketGranted: {
    type: 'flow',
    businessDescription:
      'その日に新しく付与された交換チケットの数。サブスク契約の特典や運営からの付与が含まれる。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL)',
      tables: ['user_ticket_relations'],
      logic:
        "created_at（UTC→JST変換）がその日と一致するレコード数。\nSELECT count(*) FROM user_ticket_relations WHERE (created_at AT TIME ZONE 'UTC' AT TIME ZONE 'Asia/Tokyo')::date = 対象日",
    },
  },
  ticketUsed: {
    type: 'flow',
    businessDescription:
      'その日に使用された交換チケットの数。ユーザーがチケットを使ってアイテム交換をリクエストした場合に計上される。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL)',
      tables: ['user_ticket_relations'],
      logic:
        "is_used = true かつ updated_at（UTC→JST変換）がその日と一致するレコード数。\nSELECT count(*) FROM user_ticket_relations WHERE is_used = true AND (updated_at AT TIME ZONE 'UTC' AT TIME ZONE 'Asia/Tokyo')::date = 対象日",
    },
  },
  ticketExpired: {
    type: 'flow',
    businessDescription:
      'その日に有効期限が到来して失効した交換チケットの数。未使用のまま期限切れになったチケットが対象。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL)',
      tables: ['user_ticket_relations'],
      logic:
        "is_used = false かつ expired_at（UTC→JST変換）がその日と一致するレコード数。\nSELECT count(*) FROM user_ticket_relations WHERE is_used = false AND (expired_at AT TIME ZONE 'UTC' AT TIME ZONE 'Asia/Tokyo')::date = 対象日",
    },
  },
  ticketClosing: {
    type: 'stock',
    businessDescription:
      'その日の終了時点で有効な交換チケットの総数。期首残 + 付与 − 使用 − 失効 とおおむね一致するが、JST日跨ぎのタイミングで±数件のずれが生じうる（警告レベルの検証）。',
    technicalSource: {
      database: 'aircloset_mens (PostgreSQL)',
      tables: ['user_ticket_relations'],
      logic:
        'created_at がその日以前 かつ（未使用 or 使用日がその日より後）かつ（expired_at が NULL or 失効日がその日より後）のレコード数。',
    },
    validation:
      'ticket_opening + ticket_granted − ticket_used − ticket_expired とおおむね一致（JST日跨ぎで±数件の差異は warning 止まり）',
  },
  returnCount: {
    type: 'flow',
    businessDescription:
      'その日に倉庫に到着した返却荷物の数。ユーザーが返送してから倉庫到着日（returned_date）がこの日と一致するオーダーを数える。',
    technicalSource: {
      database: 'phoenix_order (MySQL)',
      tables: ['send_back_orders', 'orders'],
      logic:
        'send_back_orders.returned_date（UTC+9h でJST変換）がその日と一致し、orders.service_id = 3（メンズ）のレコード数。\nSELECT COUNT(*) FROM send_back_orders sbo JOIN orders o ON o.id = sbo.order_id AND o.service_id = 3 WHERE DATE(sbo.returned_date + INTERVAL 9 HOUR) = 対象日',
    },
  },
  // ========== 集客ブロック ==========
  sessionCount: {
    type: 'flow',
    businessDescription:
      'その日にメンズサイト（www.air-closet.com/mens）へ訪れた回数。1人が1日に何度も訪れれば複数回数える（30分以上間があくと別の訪問になる）。広告・自然検索・直接アクセスなど流入元を問わない全体の数。会員画面（マイページ等）や広告用LPへの訪問も含む。',
    technicalSource: {
      database: 'GA4 → BigQuery (ac_mens_hub)',
      tables: ['ga4__daily_overview'],
      logic:
        'GA4 プロパティ 506839124（www.air-closet.com/mens）を日付だけで集計した sessions をそのまま採る。\nSELECT sessions FROM ac_mens_hub.ga4__daily_overview WHERE date = 対象日',
    },
    validation:
      '自然検索経由セッション数との割り算では使わないこと。参照元まで分けた ga4__acquisition の合計とは 0.1% 程度ずれる（GA4 は集計軸が細かいほどカーディナリティ上限で端が落ちるため）。比率が要るときは ga4__acquisition の中だけで計算する',
  },
  organicSessionCount: {
    type: 'flow',
    businessDescription:
      'その日の訪問のうち、検索エンジンの検索結果（広告枠ではない方）をクリックして来た回数。SEO の効果を見る指標。Google / Yahoo / Bing / DuckDuckGo / Ecosia が該当し、リスティング広告（cpc）や SNS 広告（paid_social）は含まない。',
    technicalSource: {
      database: 'GA4 → BigQuery (ac_mens_hub)',
      tables: ['ga4__acquisition'],
      logic:
        "GA4 の session_medium が 'organic' の行の sessions を日別に合計する。\nSELECT SUM(sessions) FROM ac_mens_hub.ga4__acquisition WHERE date = 対象日 AND session_medium = 'organic'",
    },
    validation:
      'セッション数（ga4__daily_overview 由来）とは参照テーブルが違うため、両者を割って比率を出すと 0.1% ずれる。厳密な自然検索比率が要るときは ga4__acquisition の全合計を分母にする',
  },
  organicGa4Sessions: {
    type: 'flow',
    businessDescription:
      '自然検索（広告枠ではない検索結果）から来た訪問の回数。グラフの「自然検索（GA4）」の CVR の分母に使う。登録完了数と同じ GA4 の集計から取るので、両者を割った値がそのまま自然検索の CVR になる。',
    technicalSource: {
      database: 'GA4 → BigQuery (ac_mens_hub)',
      tables: ['ga4__events_by_source'],
      logic:
        "session_medium が 'organic' で event_name が 'session_start' の行の sessions を日別に合計する。\nSELECT SUM(sessions) FROM ac_mens_hub.ga4__events_by_source WHERE date = 対象日 AND session_medium = 'organic' AND event_name = 'session_start'",
    },
    validation:
      '自然検索経由セッション数（ga4__acquisition 由来）とは集計元が違うため一致しない。GA4 の流入元別イベントがまだ取り込まれていない日は 0 になり、その日の CVR は表示しない',
  },
  organicGa4SubscribeComplete: {
    type: 'flow',
    businessDescription:
      '自然検索から来た訪問の中で、サブスク登録を完了した回数（GA4 の登録完了イベント）。グラフの「自然検索（GA4）」の CVR の分子。本番DBの新規サブスク登録数より数％少なく出る（タグが動かなかった分は数えられない）。',
    technicalSource: {
      database: 'GA4 → BigQuery (ac_mens_hub)',
      tables: ['ga4__events_by_source'],
      logic:
        "session_medium が 'organic' で event_name が 'mens_subscribe_complete' の行の event_count を日別に合計する。\nSELECT SUM(event_count) FROM ac_mens_hub.ga4__events_by_source WHERE date = 対象日 AND session_medium = 'organic' AND event_name = 'mens_subscribe_complete'",
    },
    validation: 'GA4 の流入元別イベントがまだ取り込まれていない日は 0',
  },
  semGa4Sessions: {
    type: 'flow',
    businessDescription:
      '検索広告（リスティング広告）から来た訪問の回数。グラフの「SEM（GA4）」の CVR の分母に使う。現状の流入は Google 広告だけ。',
    technicalSource: {
      database: 'GA4 → BigQuery (ac_mens_hub)',
      tables: ['ga4__events_by_source'],
      logic:
        "session_medium が 'cpc' で event_name が 'session_start' の行の sessions を日別に合計する。\nSELECT SUM(sessions) FROM ac_mens_hub.ga4__events_by_source WHERE date = 対象日 AND session_medium = 'cpc' AND event_name = 'session_start'",
    },
    validation:
      'GA4 の流入元別イベントがまだ取り込まれていない日は 0 になり、その日の CVR は表示しない',
  },
  semGa4SubscribeComplete: {
    type: 'flow',
    businessDescription:
      '検索広告から来た訪問の中で、サブスク登録を完了した回数（GA4 の登録完了イベント）。グラフの「SEM（GA4）」の CVR の分子。',
    technicalSource: {
      database: 'GA4 → BigQuery (ac_mens_hub)',
      tables: ['ga4__events_by_source'],
      logic:
        "session_medium が 'cpc' で event_name が 'mens_subscribe_complete' の行の event_count を日別に合計する。\nSELECT SUM(event_count) FROM ac_mens_hub.ga4__events_by_source WHERE date = 対象日 AND session_medium = 'cpc' AND event_name = 'mens_subscribe_complete'",
    },
    validation: 'GA4 の流入元別イベントがまだ取り込まれていない日は 0',
  },
};
